import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import axios from 'axios';
import { Send, Stethoscope, Brain, Dumbbell, Heart, ArrowLeft, Image, Mic, MicOff, Volume2, VolumeX, X, Paperclip, Loader2, Palette } from 'lucide-react';
import { API } from '@/lib/api';



const COMPETENCIES = [
  { icon: Stethoscope, de: 'Ernährung', en: 'Nutrition', color: 'bg-terracotta/10 text-terracotta' },
  { icon: Dumbbell, de: 'Fitness', en: 'Fitness', color: 'bg-orange-100 text-orange-600' },
  { icon: Brain, de: 'Coaching', en: 'Coaching', color: 'bg-pink-100 text-pink-600' },
  { icon: Palette, de: 'Stil & Farbe', en: 'Style & Color', color: 'bg-purple-100 text-purple-600' },
];

export default function ChatPage() {
  const { t, language } = useLanguage();
  const { user, getHeaders } = useAuth();
  const nav = useNavigate();
  const de = language === 'de';

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [attachedImage, setAttachedImage] = useState(null);
  const [recording, setRecording] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [transcribing, setTranscribing] = useState(false);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await axios.get(`${API}/chat/history`, { headers: getHeaders() });
        setMessages(res.data.length ? res.data : [{ id: 'welcome', role: 'assistant', content: t('chat.welcome') }]);
      } catch { setMessages([{ id: 'welcome', role: 'assistant', content: t('chat.welcome') }]); }
    };
    fetchHistory();
  }, []);

  useEffect(() => { scrollRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const speakText = (text) => {
    if (!ttsEnabled || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = de ? 'de-DE' : 'en-US';
    utterance.rate = 0.95;
    utterance.pitch = 1.1;
    window.speechSynthesis.speak(utterance);
  };

  const handleSend = async () => {
    if ((!input.trim() && !attachedImage) || sending) return;
    const text = input.trim();
    const image = attachedImage;
    setInput('');
    setAttachedImage(null);
    setSending(true);

    const userContent = text || (de ? '(Bild gesendet)' : '(Image sent)');
    setMessages(prev => [...prev, {
      id: `temp-${Date.now()}`, role: 'user', content: userContent,
      ...(image && { image: image })
    }]);

    try {
      const payload = { message: text || (de ? 'Analysiere dieses Bild.' : 'Analyze this image.') };
      if (image) payload.image_base64 = image;
      const res = await axios.post(`${API}/chat/message`, payload, { headers: getHeaders() });
      setMessages(prev => {
        const filtered = prev.filter(m => !m.id?.startsWith('temp-'));
        return [...filtered,
          { ...res.data.user_message, ...(image && { image }) },
          res.data.ai_response
        ];
      });
      speakText(res.data.ai_response.content);
    } catch {
      setMessages(prev => [...prev, { id: `err-${Date.now()}`, role: 'assistant', content: de ? 'Entschuldigung, etwas ist schiefgelaufen.' : 'Sorry, something went wrong.' }]);
    }
    setSending(false);
    inputRef.current?.focus();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(de ? 'Max 5 MB' : 'Max 5 MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setAttachedImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.onload = async (ev) => {
          setTranscribing(true);
          try {
            const res = await axios.post(`${API}/chat/transcribe`, { audio: ev.target.result }, { headers: getHeaders() });
            if (res.data.text) {
              setInput(prev => prev ? `${prev} ${res.data.text}` : res.data.text);
              toast.success(de ? 'Sprache erkannt' : 'Speech recognized');
            }
          } catch { toast.error(de ? 'Transkription fehlgeschlagen' : 'Transcription failed'); }
          setTranscribing(false);
        };
        reader.readAsDataURL(blob);
      };
      mediaRecorder.start();
      setRecording(true);
    } catch {
      toast.error(de ? 'Mikrofon nicht verfügbar' : 'Microphone not available');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-120px)] lg:h-[calc(100vh-80px)] max-w-3xl" data-testid="chat-page">
      {/* Header */}
      <div className="mb-3">
        <button onClick={() => nav('/dashboard')} className="flex items-center gap-1 text-foreground/40 hover:text-foreground font-body text-xs mb-2 transition-colors" data-testid="chat-back-btn">
          <ArrowLeft size={14} /> Dashboard
        </button>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 border-2 border-terracotta/20">
              <AvatarImage src="https://images.unsplash.com/photo-1722620215534-505fe1017c3e?w=100&h=100&fit=crop" />
              <AvatarFallback className="bg-terracotta/10 text-terracotta font-display">BC</AvatarFallback>
            </Avatar>
            <div>
              <h1 className="font-display text-xl font-medium">{t('chat.title')}</h1>
              <p className="font-body text-[10px] text-foreground/40">{de ? 'KI-Multi-Experten-Coach' : 'AI Multi-Expert Coach'}</p>
            </div>
          </div>
          <button onClick={() => { setTtsEnabled(!ttsEnabled); if (ttsEnabled) window.speechSynthesis?.cancel(); }}
            className={`p-2 rounded-lg transition-colors ${ttsEnabled ? 'bg-terracotta/10 text-terracotta' : 'text-foreground/30 hover:text-foreground/50'}`}
            data-testid="tts-toggle" title={de ? 'Sprachausgabe' : 'Text-to-speech'}>
            {ttsEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="coach-competencies">
          {COMPETENCIES.map((c, i) => (
            <div key={i} className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${c.color} text-[10px] font-body font-medium`} data-testid={`competency-${i}`}>
              <c.icon size={10} /> {de ? c.de : c.en}
            </div>
          ))}
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 pr-4">
        <div className="space-y-3 pb-4">
          {messages.map((msg, i) => (
            <div key={msg.id || i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`chat-message-${i}`}>
              <div className={`max-w-[85%] ${msg.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-ai'}`}>
                {msg.image && (
                  <img src={msg.image} alt="" className="w-48 h-32 object-cover rounded-lg mb-2" />
                )}
                <div className="px-4 py-2.5 font-body text-sm leading-relaxed">
                  {msg.content?.split('\n').map((line, j) => (
                    <p key={j} className={j > 0 ? 'mt-1.5' : ''}>{line}</p>
                  ))}
                </div>
                {msg.role === 'assistant' && msg.id !== 'welcome' && (
                  <button onClick={() => speakText(msg.content)} className="px-4 pb-2 text-foreground/20 hover:text-terracotta transition-colors" title={de ? 'Vorlesen' : 'Read aloud'}>
                    <Volume2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="chat-bubble-ai px-5 py-4 flex gap-1.5">
                <div className="typing-dot w-2 h-2 rounded-full bg-foreground/30" />
                <div className="typing-dot w-2 h-2 rounded-full bg-foreground/30" />
                <div className="typing-dot w-2 h-2 rounded-full bg-foreground/30" />
              </div>
            </div>
          )}
          <div ref={scrollRef} />
        </div>
      </ScrollArea>

      {/* Attached Image Preview */}
      {attachedImage && (
        <div className="flex items-center gap-2 px-3 py-2 bg-warmgray/50 rounded-t-xl border border-b-0 border-border/40">
          <img src={attachedImage} alt="" className="w-12 h-12 object-cover rounded-lg" />
          <span className="font-body text-xs text-foreground/50 flex-1">{de ? 'Bild angehängt' : 'Image attached'}</span>
          <button onClick={() => setAttachedImage(null)} className="text-foreground/30 hover:text-red-500"><X size={16} /></button>
        </div>
      )}

      {/* Input Bar */}
      <div className="flex items-center gap-2 mt-2 pb-16 lg:pb-0">
        {/* File Upload */}
        <button onClick={() => fileRef.current?.click()} className="p-2.5 rounded-xl text-foreground/40 hover:text-terracotta hover:bg-terracotta/5 transition-colors" data-testid="chat-upload-btn" title={de ? 'Foto/Datei' : 'Photo/File'}>
          <Paperclip size={20} />
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />

        {/* Mic */}
        <button
          onClick={recording ? stopRecording : startRecording}
          disabled={transcribing}
          className={`p-2.5 rounded-xl transition-all ${recording ? 'bg-red-500 text-white animate-pulse' : transcribing ? 'bg-amber-100 text-amber-600' : 'text-foreground/40 hover:text-terracotta hover:bg-terracotta/5'}`}
          data-testid="chat-mic-btn" title={de ? 'Spracheingabe' : 'Voice input'}>
          {transcribing ? <Loader2 size={20} className="animate-spin" /> : recording ? <MicOff size={20} /> : <Mic size={20} />}
        </button>

        {/* Text Input */}
        <Input ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
          placeholder={recording ? (de ? 'Aufnahme läuft...' : 'Recording...') : t('chat.placeholder')}
          className="flex-1 rounded-full border-border/80 py-5 px-5 font-body shadow-sm"
          disabled={recording}
          data-testid="chat-input" />

        {/* Send */}
        <Button onClick={handleSend} disabled={sending || (!input.trim() && !attachedImage)}
          className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white w-11 h-11 p-0 shadow-lg shadow-terracotta/20"
          data-testid="chat-send-btn">
          <Send size={18} />
        </Button>
      </div>
    </div>
  );
}
